import { Config, EventConfig, GameTypes } from "kadow-jetpack-model";
import { GameThumbnailDTO } from "../../model/GameDTO";
import { DEFAULT_EVENTS } from "../constants";
import { buildGameThumbnailDTO } from "./buildGameThumbnailDTO";
import { isPerformanceGame } from "kadow-jetpack-model";
import { isPerformanceGameHiddenByDisabledMonthlyLottery } from "./performanceGameAvailability";
import TSUtils from "./TSUtils";

const FIRST_MATCHING_EVENT_PERFORMANCE_GAMES = [
  GameTypes.PenaltyKick,
  GameTypes.Prognosis,
  GameTypes.SpaceInvaders,
  GameTypes.Breakout,
  GameTypes.Quiz
];

export function buildGamesList(
  includeGamesOfChance: boolean,
  includePerformanceGames: boolean,
  includeSkins: boolean,
  config: Config | null | undefined,
  preserveExistingGames: boolean,
  eventsParam: EventConfig[] | null,
  isUpsell: boolean
): GameThumbnailDTO[] {
  let list: GameThumbnailDTO[] = [];

  let events: EventConfig[] = [...DEFAULT_EVENTS];

  const hasFirstMatchingEventPerformanceGameSkin =
    includePerformanceGames &&
    !!eventsParam?.some((ev) =>
      ev.skins?.some((s) =>
        FIRST_MATCHING_EVENT_PERFORMANCE_GAMES.includes(s.game)
      )
    );

  if (includeSkins && eventsParam && eventsParam.length > 0) {
    if (includeSkins) {
      eventsParam?.forEach((ev) => {
        if (!events.some((e) => e._id === ev._id)) {
          events.push(ev);
        }
      });
    }
  }

  if (hasFirstMatchingEventPerformanceGameSkin) {
    FIRST_MATCHING_EVENT_PERFORMANCE_GAMES.forEach((game) => {
      const firstEventWithSkin = eventsParam?.find((ev) =>
        ev.skins?.some((s) => s.game === game)
      );
      if (firstEventWithSkin) {
        buildGameThumbnailDTO(list, eventsParam, firstEventWithSkin._id, game);
      }
    });
  }

  // Edge-case : if both flags are false, only propose the default Fortune Wheel skin
  if (!includeGamesOfChance && !includePerformanceGames) {
    buildGameThumbnailDTO(list, events, "default", GameTypes.FortuneWheel);
    return list;
  }

  Object.values(GameTypes).forEach((type) => {
    if (!preserveExistingGames && config?.games?.find((g) => g.name === type)) {
      return;
    }

    const isPerfGame = type !== GameTypes.KadowCrush && isPerformanceGame(type);
    const isGameOfChance = !isPerfGame;

    if (isGameOfChance && !includeGamesOfChance) {
      return;
    }

    if (isPerfGame && !includePerformanceGames) {
      return;
    }

    if (isPerformanceGameHiddenByDisabledMonthlyLottery(type, config)) {
      return;
    }

    const addForAllEvents = (supportedType: GameTypes) => {
      events.forEach((ev) => {
        buildGameThumbnailDTO(list, events, ev._id, supportedType);
      });
    };

    switch (type) {
      case GameTypes.Dice:
      case GameTypes.ScratchCard:
      case GameTypes.FlipCards:
      case GameTypes.Fakir:
      case GameTypes.MysteryBoxes:
      case GameTypes.FortuneWheel:
      case GameTypes.SlotMachine:
      case GameTypes.Puzzle:
      case GameTypes.Catcher:
      case GameTypes.BalloonBlast:
      case GameTypes.KadowCrush:
      case GameTypes.Prognosis:
      case GameTypes.PenaltyKick:
      case GameTypes.Breakout:
      case GameTypes.SpaceInvaders:
      case GameTypes.Wordle:
      case GameTypes.Jumper:
      case GameTypes.Quiz:
        addForAllEvents(type);
        break;

      case GameTypes.MonthlyLottery:
        return;
    }
  });

  list = TSUtils.shuffleArray(list);

  if (isUpsell && includePerformanceGames && list.length) {
    list = TSUtils.shuffleArray(list);
    const visited: Set<string> = new Set();

    for (let idx = list.length - 1; idx >= 0; idx--) {
      const g = list[idx];

      if (g.eventId === "default") {
        // remove default games that already exists in the config
        if (
          !isPerformanceGame(g.name as GameTypes) &&
          config?.games?.find((cg) => cg.name === g.name)
        ) {
          list.splice(idx, 1);
        }
        continue;
      }

      if (visited.has(g.eventId)) {
        list.splice(idx, 1);
        continue;
      }

      visited.add(g.eventId);
    }
  }

  list = list.filter(
    (game, index) =>
      list.findIndex(
        (other) => other.name === game.name && other.eventId === game.eventId
      ) === index
  );

  return list;
}
